// 3rd Party
import React, { ReactElement, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import 'react-day-picker/lib/style.css';
import { useTranslation } from 'react-i18next';

// Components
import { Button, CustomSelect, Input, Typography } from 'components';

// Types
import { ChangeEvent, Machine } from 'types';
import { MaintenanceCreator, MaintenanceEvent, MaintenanceEventStatus } from 'types/maintenance';

// Api
import { useCreateServiceTechMutation } from 'api';

// Helpers
import { toISO8601 } from 'common/helpers/v1';
import RequiredMark from 'components/RequiredMarker';
import theme from 'themes';
import { ErrorMessage } from '../AssignAndScheduleEvent';
import { ToastMsg } from 'common/components/Toast';
import { useUser } from 'selectors';
import { BtnContainer, Footer } from 'pages/UserManagementTwo/components/User/UserModal';

interface FollowUpDetailProps {
  maintenanceEvent?: MaintenanceEvent;
  machines?: Machine[];
  machineId?: string;
  onClose: () => void;
  onSubmitClick: () => void;
  newService?: boolean;
}

// Styling
const Container = styled.div`
  padding: 1rem 0;
  background-color: ${({ theme }) => theme.colors.white};
  flex: 1;
`;
const DateField = styled.div`
  display: flex;
  align-items: center;
  > :first-child {
    width: 70%;
  }
`;
const DateWithError = styled.div`
  width: 100%;
`;

const InputGroup = styled.div`
  margin-top: 2rem;
  margin-bottom: 0.75rem;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 0.0625rem solid ${({ theme }) => theme.colors.lightGrey6};
  box-shadow: ${({ theme }) => theme.colors.borders.border02.shadow};
  border-radius: 0.375rem;
`;

const defaultOption = {
  label: 'Select...',
  value: undefined
};
const priorityOptions = [
  defaultOption,
  {
    label: '1',
    value: '1'
  },
  {
    label: '2',
    value: '2'
  },
  {
    label: '3',
    value: '3'
  }
];
const today = new Date().setHours(0, 0, 0, 0);
const DefaultMaintenanceEvent: MaintenanceEvent = {
  id: '',
  description: '',
  subcomponent: '',
  suggestedDue: new Date(today),
  machineId: '',
  machineDescription: '',
  priority: 0,
  status: MaintenanceEventStatus.NotComplete,
  creator: MaintenanceCreator.User,
  owner: '',
  completionCycleCount: 0
};
const FollowUpDetail = ({
  maintenanceEvent,
  onClose,
  onSubmitClick,
  machines,
  machineId,
  newService
}: FollowUpDetailProps): ReactElement => {
  const user = useUser();
  const [pmDetails, setPMDetails] = useState<Partial<MaintenanceEvent>>(
    maintenanceEvent ? maintenanceEvent : DefaultMaintenanceEvent
  );
  const getMachineById = useCallback(
    (id: string) => {
      const match = machines?.find((machine) => machine.id === id);
      match &&
        setPMDetails((pmDetails) => ({
          ...pmDetails,
          machineId: id,
          machineDescription: match.description
        }));
    },
    [machines]
  );

  useEffect(() => {
    if (machineId || pmDetails.machineId) {
      getMachineById(machineId || (pmDetails?.machineId as string));
    } else {
      setPMDetails((pmDetails) => ({
        ...pmDetails,
        machineId: undefined,
        machineDescription: undefined
      }));
    }
  }, [getMachineById, machineId]);

  const { t } = useTranslation(['fpns', 'common']);
  const isValid = (): boolean => {
    const valid = Boolean(
      pmDetails?.priority &&
        pmDetails?.machineDescription &&
        pmDetails?.subcomponent &&
        isValidDate &&
        pmDetails?.machineId &&
        pmDetails?.description?.trim()
    );
    return valid;
  };
  type propNames =
    | 'priority'
    | 'machineDescription'
    | 'subcomponent'
    | 'description'
    | 'suggestedDue'
    | 'machineId';

  const handlePMDetailsUpdate = (pm: MaintenanceEvent) => {
    setPMDetails(pm);
  };

  const getMachineId = (id: string) => {
    const selectedMachine = machines?.find((machine) => machine.id === id);
    if (selectedMachine) {
      setPMDetails({
        ...pmDetails,
        machineId: selectedMachine.id,
        machineDescription: selectedMachine.description
      });
    } else {
      setPMDetails({ ...pmDetails, machineId: '', machineDescription: '' });
    }
  };

  // Needed to manipulate Date into type String
  const copyPMInfo = (
    pm: Partial<MaintenanceEvent> | undefined,
    propName: propNames,
    value: string | number | Date | boolean
  ) => {
    const copyPMDetails = { ...pmDetails, [propName]: value } as MaintenanceEvent;
    handlePMDetailsUpdate(copyPMDetails);
  };

  const handleChange = (value: string | Date | number | boolean, propName: propNames) => {
    setPMDetails({ ...pmDetails, [propName]: value });
  };

  const handleDayChange = (selectedDay: Date) => {
    copyPMInfo(pmDetails, 'suggestedDue', toISO8601(selectedDay));
  };

  const [createEvent] = useCreateServiceTechMutation();
  const isValidDate = new Date(pmDetails.suggestedDue || '') >= new Date(today);
  const validateSubComponent = (word: string): boolean => {
    // Check if word starts with empty space
    if (word?.startsWith(' ')) {
      return false;
    }

    // Check for multiple consecutive spaces
    if (word?.includes('  ')) {
      return false;
    }

    // Check if word contains any special characters, hyphens, or underscores
    // This regex allows only letters (a-z, A-Z), numbers (0-9), and single spaces
    const validPattern = /^[a-zA-Z0-9 ]*$/;

    return validPattern.test(word);
  };

  return (
    <Container>
      <Typography mb={'.2rem'} size="1rem" weight="semi-bold">
        {t('priority_level')} <RequiredMark color={theme.colors.text.error} size={'1.3rem'} />
      </Typography>
      <CustomSelect
        variant={'white'}
        mandatory={false}
        onChange={(option): void => {
          handleChange(option.value, 'priority');
        }}
        options={priorityOptions}
        placeholder={t('select_dot', { ns: 'common' }) as string}
        id="priority"
        isSearchable={false}
      />{' '}
      <br />
      <Typography mb={'.2rem'} size="1rem" weight="semi-bold">
        {t('machine')} <RequiredMark color={theme.colors.text.error} size={'1.3rem'} />
      </Typography>
      {!newService ? (
        <Input
          type="string"
          id="machineDescription"
          variant="white"
          value={pmDetails?.machineDescription}
          onChange={(event: ChangeEvent) => {
            handleChange(event.target.value, 'machineDescription');
          }}
        />
      ) : (
        <CustomSelect
          variant={'white'}
          mandatory
          onChange={(option) => {
            getMachineId(option.value);
          }}
          value={
            pmDetails.machineId
              ? [
                  {
                    label: pmDetails.machineDescription,
                    value: pmDetails.machineId
                  }
                ]
              : []
          }
          options={
            machines
              ? [...machines].map((m) => ({
                  label: m.description || m.label,
                  value: m.value || m.id
                }))
              : []
          }
          placeholder={t('select_dot', { ns: 'common' }) as string}
          id="machine"
        />
      )}
      <br />
      <Typography mb={'.2rem'} size="1rem" weight="semi-bold">
        {t('subcomponent')} <RequiredMark color={theme.colors.text.error} size={'1.3rem'} />
      </Typography>
      <Input
        type="string"
        id="subcomponent"
        variant="white"
        value={pmDetails.subcomponent}
        onChange={(event: ChangeEvent) => {
          validateSubComponent(event.target.value) &&
            handleChange(event.target.value, 'subcomponent');
        }}
      />
      <br /> <br />
      <DateField>
        <Typography mb={'.2rem'} size="1rem" weight="semi-bold">
          {t('suggested_date')}
        </Typography>
        <DateWithError>
          <Input
            type="date"
            max="9999-12-31"
            onChange={(e: ChangeEvent) => {
              handleDayChange(e.target.value as unknown as Date);
            }}
            min={new Date().toISOString().split('T')[0]}
          />
          {!isValidDate && <ErrorMessage>{`Date must be today's or later`}</ErrorMessage>}
        </DateWithError>
      </DateField>
      <InputGroup>
        <Typography mb={'.2rem'} size="1rem" weight="semi-bold">
          {t('description', { ns: 'common' })}{' '}
          <RequiredMark color={theme.colors.text.error} size={'1.3rem'} />
        </Typography>
        <StyledTextarea
          rows={6}
          value={pmDetails?.description}
          onChange={(event) => {
            if (event.target.value.length <= 1000)
              handleChange(event.target.value.trimStart() as string, 'description');
          }}
        />
        <Typography>1000 characters max</Typography>
      </InputGroup>
      <Footer className="service-footer">
        <BtnContainer>
          <Button
            variant="link"
            borderRadius="3rem"
            borderColor={theme.colors.primaryBlue5}
            width="6.688rem"
            height="2rem"
            onClick={onClose}
          >
            {t('cancel', { ns: 'common' })}
          </Button>
          <Button
            variant="hover-blue"
            borderRadius="3rem"
            bgColor={theme.colors.primaryBlue5}
            color={theme.colors.lightGrey3}
            width="6.688rem"
            height="2rem"
            disabled={!isValid()}
            onClick={() => {
              createEvent({
                description: pmDetails?.description,
                machineID: pmDetails?.machineId,
                subcomponent: pmDetails?.subcomponent?.trim(),
                suggestedDue: pmDetails?.suggestedDue,
                status: MaintenanceEventStatus.NotComplete,
                creator: MaintenanceCreator.User,
                owner: user?.id,
                priority: pmDetails.priority
              }).then(() => {
                ToastMsg({
                  message: t('maintenance_event_created'),
                  heading: t('created') as string,
                  theme: 'colored',
                  type: 'success'
                });
                onSubmitClick();
                onClose();
              });
            }}
          >
            {t('confirm', { ns: 'common' })}
          </Button>
        </BtnContainer>
      </Footer>
    </Container>
  );
};

export default FollowUpDetail;
